import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface PriceModalItem {
  product_id: string;
  product_name: string;
  price: number | null;
  price_source: string | null;
  last_purchased_at: string | null;
  catalog_price: number | null;
}

interface PriceModalProps {
  items: PriceModalItem[];
  customerName: string;
  onSave: (prices: Record<string, number>) => void;
  onClose: () => void;
}

export default function PriceModal({ items, customerName, onSave, onClose }: PriceModalProps) {
  const { t } = useTranslation();
  const [prices, setPrices] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const item of items) {
      initial[item.product_id] = item.price != null ? String(item.price) : '';
    }
    return initial;
  });

  const handleSave = () => {
    const result: Record<string, number> = {};
    for (const item of items) {
      const val = parseFloat(prices[item.product_id]);
      if (!isNaN(val) && val >= 0) {
        result[item.product_id] = val;
      }
    }
    onSave(result);
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString();
    } catch {
      return iso;
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-5 pb-3 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">
            {t('priceModal.title')}
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            {t('priceModal.subtitle', { customer: customerName })}
          </p>
        </div>

        {/* Items list */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {items.map((item) => (
            <div key={item.product_id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-gray-900">{item.product_name}</span>
              </div>

              {/* Price hints */}
              <div className="text-xs text-gray-500 mb-3 space-y-0.5">
                {item.last_purchased_at && item.price_source === 'customer_history' && (
                  <p className="text-blue-600">
                    {t('priceModal.lastPurchased', {
                      price: item.price?.toFixed(2),
                      date: formatDate(item.last_purchased_at),
                    })}
                  </p>
                )}
                {item.catalog_price != null && item.catalog_price > 0 && (
                  <p className="text-gray-500">
                    {t('priceModal.catalogPrice', { price: item.catalog_price.toFixed(2) })}
                  </p>
                )}
                {!item.last_purchased_at && (item.catalog_price == null || item.catalog_price === 0) && (
                  <p className="text-amber-600 italic">
                    {t('priceModal.noHistoricalPrice')}
                  </p>
                )}
              </div>

              {/* Price input */}
              <div className="flex items-center gap-2">
                <span className="text-gray-400 text-sm">₪</span>
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  value={prices[item.product_id]}
                  onChange={(e) =>
                    setPrices((prev) => ({ ...prev, [item.product_id]: e.target.value }))
                  }
                  placeholder={t('priceModal.enterPrice')}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-100 transition"
          >
            {t('priceModal.skip')}
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition font-medium"
          >
            {t('priceModal.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
